import { Columns2, Eye, EyeOff, FileCode2 } from 'lucide-react';
import { PayloadInspector } from '@/components/PayloadInspector';
import { cn } from '@/lib/cn';
import { ToggleTab } from './ToggleTab';
import type { Translate } from './types';

export type GenericPaneMode = 'primary' | 'pair' | 'both';

export function GenericEditorPanes({
  primaryShort,
  primaryContent,
  pairShort,
  pairContent,
  paneMode,
  setPaneMode,
  onEditPrimary,
  onEditPair,
  t,
}: {
  primaryShort: string;
  primaryContent: string;
  pairShort?: string;
  pairContent?: string;
  paneMode: GenericPaneMode;
  setPaneMode: (mode: GenericPaneMode) => void;
  onEditPrimary: (xml: string) => void;
  onEditPair?: (xml: string) => void;
  t: Translate;
}) {
  const hasPair = Boolean(pairShort && pairContent);
  const showPrimary = paneMode === 'primary' || paneMode === 'both';
  const showPair = hasPair && (paneMode === 'pair' || paneMode === 'both');
  const isPrimary = paneMode === 'primary';
  const isPair = paneMode === 'pair';
  const isBoth = paneMode === 'both';
  const responseVisible = showPair;

  return (
    <div className="flex min-h-[28rem] min-w-0 flex-col gap-3 xl:sticky xl:top-[calc(53px+1rem)] xl:h-[calc(100dvh-53px-2rem)] xl:max-h-[calc(100dvh-53px-2rem)]">
      {hasPair && (
        <div className="flex flex-wrap items-center gap-2">
          {responseVisible ? (
            <div className="flex flex-wrap gap-px" role="group" aria-label={t('try.paneMode')}>
              <ToggleTab active={isPrimary} onClick={() => setPaneMode('primary')}>
                <FileCode2 size={12} aria-hidden /> {primaryShort}
              </ToggleTab>
              <ToggleTab active={isPair} onClick={() => setPaneMode('pair')}>
                <FileCode2 size={12} aria-hidden /> {pairShort}
              </ToggleTab>
              <ToggleTab active={isBoth} onClick={() => setPaneMode('both')}>
                <Columns2 size={12} aria-hidden /> {t('try.paneBoth')}
              </ToggleTab>
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => setPaneMode(responseVisible ? 'primary' : 'both')}
            className="inline-flex items-center gap-1.5 border border-rule bg-surface px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest text-muted hover:border-ink hover:text-ink"
          >
            {responseVisible ? <EyeOff size={12} aria-hidden /> : <Eye size={12} aria-hidden />}
            {responseVisible
              ? t('try.hideResponse', { short: pairShort! })
              : t('try.showResponse', { short: pairShort! })}
          </button>
        </div>
      )}

      <div
        className={cn(
          'grid min-h-0 flex-1 gap-3',
          paneMode === 'both' && hasPair && 'lg:grid-cols-2',
        )}
      >
        {showPrimary && (
          <div className="min-h-[420px] min-w-0 xl:min-h-0">
            <PayloadInspector
              content={primaryContent}
              format="xml"
              title={t('try.titlePrimary', { short: primaryShort })}
              description={t('try.editPrimary')}
              onContentChange={onEditPrimary}
            />
          </div>
        )}
        {showPair && pairContent && (
          <div className="min-h-[420px] min-w-0 xl:min-h-0">
            <PayloadInspector
              content={pairContent}
              format="xml"
              title={t('try.titlePair', { short: pairShort! })}
              description={t('try.editPair')}
              onContentChange={onEditPair}
            />
          </div>
        )}
      </div>
    </div>
  );
}
